import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface InlineEditableValueProps {
    /** Current value */
    value: number;
    /** Callback when value changes */
    onChange: (value: number) => void;
    /** Format type for display */
    format: 'percent' | 'currency' | 'years';
    /** Currency code (required for 'currency' format) */
    currency?: string;
    /** Minimum allowed value */
    min?: number;
    /** Maximum allowed value */
    max?: number;
    /** Step increment for input */
    step?: number;
}

/**
 * Inline editable value component - displays a value as a styled pill
 * that transforms into an input when clicked.
 *
 * Uses absolute positioning to prevent layout shift when toggling edit mode.
 */
export function InlineEditableValue({
    value,
    onChange,
    format,
    currency,
    min,
    max,
    step = format === 'percent' ? 0.1 : format === 'years' ? 1 : 100,
}: InlineEditableValueProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const { settings } = useAuth();

    // Format value for display based on format type
    const formatDisplayValue = useCallback((val: number): string => {
        if (format === 'percent') {
            return `${(val * 100).toFixed(1)}%`;
        }
        if (format === 'years') {
            return `${Math.round(val)}`;
        }
        if (format === 'currency') {
            const displayCurrency = currency || settings?.displayCurrency || 'USD';
            const formatted = new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: displayCurrency,
                maximumFractionDigits: 0,
            }).format(val);
            return formatted;
        }
        return String(val);
    }, [format, currency, settings?.displayCurrency]);

    // Get raw value for input based on format type
    const getInputValue = useCallback((val: number): string => {
        if (format === 'percent') {
            return (val * 100).toFixed(1);
        }
        if (format === 'years') {
            return String(Math.round(val));
        }
        return String(Math.round(val));
    }, [format]);

    // Parse input value back to internal format
    const parseInputValue = useCallback((inputVal: string): number | null => {
        const parsed = parseFloat(inputVal);
        if (isNaN(parsed)) return null;

        if (format === 'percent') {
            return parsed / 100;
        }
        return parsed;
    }, [format]);

    // Start editing
    const handleStartEdit = useCallback(() => {
        setEditValue(getInputValue(value));
        setIsEditing(true);
    }, [value, getInputValue]);

    // Save value
    const handleSave = useCallback(() => {
        const parsed = parseInputValue(editValue);
        if (parsed !== null) {
            // Apply min/max bounds
            let bounded = parsed;
            if (min !== undefined) bounded = Math.max(min, bounded);
            if (max !== undefined) bounded = Math.min(max, bounded);
            // Compare formatted values to avoid floating point precision issues
            if (getInputValue(bounded) !== getInputValue(value)) {
                onChange(bounded);
            }
        }
        setIsEditing(false);
    }, [editValue, parseInputValue, getInputValue, min, max, onChange, value]);

    // Cancel editing
    const handleCancel = useCallback(() => {
        setIsEditing(false);
    }, []);

    // Handle key press
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    }, [handleSave, handleCancel]);

    // Focus input when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const displayValue = formatDisplayValue(value);

    // Container with relative positioning - the button always defines the size
    // Input is absolutely positioned to overlay without causing layout shift
    return (
        <span className="relative inline-flex">
            {/* Button always rendered to maintain consistent size */}
            <button
                type="button"
                onClick={handleStartEdit}
                className={`inline-flex items-center justify-center
                    bg-accent/10 dark:bg-accent/20
                    text-accent dark:text-accent
                    px-1.5 py-0.5 rounded
                    font-medium cursor-pointer
                    hover:bg-accent/20 dark:hover:bg-accent/30
                    focus:outline-hidden focus:ring-2 focus:ring-accent focus:ring-offset-1
                    dark:focus:ring-offset-gray-800
                    transition-colors text-sm
                    ${format === 'currency' ? 'min-w-20' : ''}
                    ${isEditing ? 'invisible' : ''}`}
                title="Click to edit"
                tabIndex={isEditing ? -1 : 0}
            >
                {displayValue}
            </button>

            {/* Input overlay - absolutely positioned to prevent layout shift */}
            {isEditing && (
                <input
                    ref={inputRef}
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    step={format === 'percent' ? 0.1 : step}
                    min={format === 'percent' && min !== undefined ? min * 100 : undefined}
                    max={format === 'percent' && max !== undefined ? max * 100 : undefined}
                    className="absolute inset-0 w-full px-1.5 py-0.5 text-sm font-medium text-center
                        border border-accent dark:border-gray-700
                        rounded focus:outline-hidden focus:ring-1 focus:ring-accent
                        bg-white dark:bg-gray-800
                        text-gray-900 dark:text-gray-100
                        [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
            )}
        </span>
    );
}
